import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Bell, PartyPopper, UserPlus, Check, Inbox, Mail, UserCog, Network, Trash2,
  Settings as Cog, Loader2, CheckCheck, Megaphone,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  announcement: Megaphone,
  squad_celebration: PartyPopper,
  squad_join_request: UserPlus,
  squad_request_approved: Check,
  squad_removed: Bell,
  agency_invite: Mail,
  agency_member_joined: UserPlus,
  agency_recruiter_joined: UserCog,
  agency_team_head: Network,
};

const FILTERS = [
  { k: "all", l: "All" },
  { k: "unread", l: "Unread" },
  { k: "announcements", l: "Announcements" },
  { k: "squads", l: "Squads" },
  { k: "agencies", l: "Agencies" },
];

const PREF_GROUPS = [
  {
    title: "In-app notifications", icon: Bell,
    items: [
      { k: "squad_celebrations_inapp", l: "Squad celebrations", d: "When a Squad-mate lands a job" },
      { k: "squad_activity_inapp", l: "Squad activity", d: "Join requests, approvals and removals" },
      { k: "agency_invites_inapp", l: "Agency invites", d: "When an agency invites you to join" },
      { k: "agency_activity_inapp", l: "Agency activity", d: "Members joining, recruiters, team-head assignments" },
    ],
  },
  {
    title: "Email notifications", icon: Mail,
    items: [
      { k: "squad_celebrations_email", l: "Squad celebration emails", d: "Get an email when a Squad-mate celebrates a win" },
      { k: "agency_reports_email", l: "Agency report emails", d: "Scheduled agency / team performance reports" },
    ],
  },
];

const chip = (active) => ({
  padding: "0.42rem 0.9rem", borderRadius: 999, fontSize: "0.82rem", cursor: "pointer",
  border: `1.5px solid ${active ? "#6366f1" : "var(--border-main)"}`,
  background: active ? "rgba(99,102,241,0.15)" : "transparent",
  color: active ? "#6366f1" : "var(--text-secondary)",
  fontWeight: active ? 600 : 400, transition: "all 0.15s",
});

const Toggle = ({ on, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick}
    style={{ width: 44, height: 25, borderRadius: 999, border: "none", cursor: "pointer", flexShrink: 0,
      background: on ? "#6366f1" : "var(--border-main)", position: "relative", transition: "background 0.15s" }}>
    <span style={{ position: "absolute", top: 3, left: on ? 22 : 3, width: 19, height: 19, borderRadius: "50%", background: "#fff", transition: "left 0.15s" }} />
  </button>
);

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [view, setView] = useState("inbox");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [prefs, setPrefs] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/social/notifications?limit=200`);
      setItems(r.data.notifications || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/social/notification-prefs`).then((r) => setPrefs(r.data.prefs)).catch(() => {});
  }, []);

  const filtered = items.filter((n) => {
    if (filter === "unread") return !n.read;
    if (filter === "announcements") return n.type === "announcement";
    if (filter === "squads") return (n.type || "").startsWith("squad");
    if (filter === "agencies") return (n.type || "").startsWith("agency");
    return true;
  });
  const unreadCount = items.filter((n) => !n.read).length;

  const openItem = async (n) => {
    if (!n.read) { try { await axios.post(`${API}/social/notifications/read`, { ids: [n.id] }); } catch { /* ignore */ } setItems((it) => it.map((x) => x.id === n.id ? { ...x, read: true } : x)); }
    if (n.link) navigate(n.link);
  };
  const markAll = async () => {
    try { await axios.post(`${API}/social/notifications/read`, {}); setItems((it) => it.map((n) => ({ ...n, read: true }))); toast.success("All marked read"); } catch { toast.error("Failed"); }
  };
  const deleteOne = async (n) => {
    try { await axios.post(`${API}/social/notifications/delete`, { ids: [n.id] }); setItems((it) => it.filter((x) => x.id !== n.id)); } catch { toast.error("Failed"); }
  };
  const clearAll = async () => {
    if (!window.confirm("Clear all notifications?")) return;
    try { await axios.post(`${API}/social/notifications/delete`, { all: true }); setItems([]); toast.success("Cleared"); } catch { toast.error("Failed"); }
  };
  const togglePref = async (k) => {
    const next = { ...prefs, [k]: !prefs[k] };
    setPrefs(next);
    try { await axios.put(`${API}/social/notification-prefs`, { prefs: { [k]: next[k] } }); }
    catch { toast.error("Failed to save"); setPrefs(prefs); }
  };

  return (
    <div className="page-container fade-in" data-testid="notifications-page">
      <div className="page-header">
        <div>
          <h1 style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}><Bell size={24} /> Notifications</h1>
          <p style={{ color: "var(--text-secondary)" }}>Your Squad and Agency activity, all in one place.</p>
        </div>
      </div>

      {/* View tabs */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", borderBottom: "1px solid var(--border-main)", marginBottom: "1.2rem" }}>
        {[{ k: "inbox", l: `Inbox${unreadCount ? ` (${unreadCount})` : ""}`, icon: Inbox }, { k: "prefs", l: "Preferences", icon: Cog }].map(({ k, l, icon: Icon }) => (
          <button key={k} data-testid={`notif-view-${k}`} onClick={() => setView(k)}
            style={{ background: "none", border: "none", borderBottom: `2px solid ${view === k ? "#6366f1" : "transparent"}`,
              color: view === k ? "var(--text-primary)" : "var(--text-secondary)", padding: "0.6rem 0.9rem", cursor: "pointer",
              fontWeight: view === k ? 600 : 400, display: "flex", alignItems: "center", gap: 6, fontSize: "0.88rem" }}>
            <Icon size={15} /> {l}
          </button>
        ))}
      </div>

      {/* INBOX */}
      {view === "inbox" && (
        <div data-testid="notif-inbox">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: "1rem" }}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {FILTERS.map((f) => <button key={f.k} data-testid={`notif-filter-${f.k}`} onClick={() => setFilter(f.k)} style={chip(filter === f.k)}>{f.l}</button>)}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn-secondary" data-testid="notif-mark-all" onClick={markAll} disabled={unreadCount === 0}><CheckCheck size={14} /> Mark all read</button>
              <button className="btn-secondary" data-testid="notif-clear-all" onClick={clearAll} disabled={items.length === 0} style={{ color: "#ef4444" }}><Trash2 size={14} /> Clear all</button>
            </div>
          </div>

          {loading ? <div style={{ color: "var(--text-secondary)" }}><Loader2 size={15} className="spin" /> Loading…</div>
            : filtered.length === 0 ? (
              <div className="card" data-testid="notif-empty" style={{ padding: "3rem", textAlign: "center", color: "var(--text-muted)" }}>
                <Inbox size={36} style={{ opacity: 0.4 }} />
                <p>You're all caught up.</p>
              </div>
            ) : filtered.map((n) => {
              const Icon = ICONS[n.type] || Bell;
              return (
                <div key={n.id} className="card" data-testid={`notif-item-${n.id}`}
                  style={{ padding: "0.9rem 1.1rem", display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 8,
                    background: n.read ? "var(--bg-surface)" : "rgba(99,102,241,0.08)", borderColor: n.read ? "var(--border-main)" : "rgba(99,102,241,0.35)" }}>
                  <Icon size={20} style={{ color: "#6366f1", flexShrink: 0, marginTop: 2 }} />
                  <div style={{ flex: 1, minWidth: 0, cursor: n.link ? "pointer" : "default" }} onClick={() => openItem(n)}>
                    <div style={{ fontSize: "0.9rem", fontWeight: n.read ? 400 : 600 }}>{n.title}</div>
                    {n.body && <div style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>{n.body}</div>}
                    {n.from_name && <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 2, fontStyle: "italic" }}>— {n.from_name}</div>}
                    <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 3 }}>{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                  {!n.read && <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#6366f1", flexShrink: 0, marginTop: 6 }} />}
                  <button data-testid={`notif-delete-${n.id}`} onClick={() => deleteOne(n)} title="Delete"
                    style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", flexShrink: 0 }}><Trash2 size={15} /></button>
                </div>
              );
            })}
        </div>
      )}

      {/* PREFERENCES */}
      {view === "prefs" && (
        <div data-testid="notif-prefs">
          {!prefs ? <div style={{ color: "var(--text-secondary)" }}><Loader2 size={15} className="spin" /> Loading…</div>
            : PREF_GROUPS.map((g) => (
              <div key={g.title} className="card" style={{ padding: "1.3rem", marginBottom: "1rem" }}>
                <h3 style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 6 }}><g.icon size={18} /> {g.title}</h3>
                {g.items.map((it, i) => (
                  <div key={it.k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, padding: "0.8rem 0", borderTop: i ? "1px solid var(--border-main)" : "none" }}>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: "0.88rem" }}>{it.l}</div>
                      <div style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>{it.d}</div>
                    </div>
                    <Toggle on={!!prefs[it.k]} onClick={() => togglePref(it.k)} testid={`pref-${it.k}`} />
                  </div>
                ))}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
